import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { getFirebaseInstances } from './config/firebase/firebase';
import { AuthProvider } from './components/common/AuthProvider';
import ProtectedRoute from './components/common/ProtectedRoute';
import Login from './components/common/Login';
import Signup from './components/common/Signup';
import PasswordGate from './components/common/PasswordGate';
import SignupFinalizationRecovery from './components/common/SignupFinalizationRecovery';
import AuthActionHandler, { VerifyEmailRedirect } from './components/common/auth/AuthActionHandler';
import ProfileCompletion from './components/common/auth/ProfileCompletion';
import HomePage from './pages/public/HomePage';
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import TermsOfService from './pages/public/TermsOfService';

const LegalTerminal = lazy(() => import('./pages/terminal/LegalTerminal'));
const PanelPage = lazy(() => import('./pages/panel/PanelPage'));

function App() {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        await getFirebaseInstances();
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize Firebase:', error);
        // Still allow app to load even if Firebase fails
        setIsInitialized(true);
      }
    };

    initializeApp();
  }, []);

  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-[#F5F0E7] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#28231F] mx-auto"></div>
          <p className="mt-2 text-[#62584E]">Loading VerdeKai Legal Terminal...</p>
        </div>
      </div>
    );
  }

  const routeFallback = (
    <div className="min-h-screen bg-[#F5F0E7] flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#28231F] mx-auto"></div>
        <p className="mt-2 text-[#62584E]">Loading workspace module...</p>
      </div>
    </div>
  );

  return (
    <AuthProvider>
        <Router>
          <Suspense fallback={routeFallback}>
            <Routes>
              {/* Main Terminal Route */}
              <Route
                path="/terminal"
                element={
                  <ProtectedRoute>
                    <LegalTerminal />
                  </ProtectedRoute>
                }
              />

              <Route path="/messages" element={<Navigate to="/terminal" replace />} />

              {/* Panel Pop-Out Route - Standalone panel windows */}
              <Route
                path="/panel"
                element={
                  <ProtectedRoute>
                    <PanelPage />
                  </ProtectedRoute>
                }
              />

              {/* Login Route */}
              <Route path="/login" element={<Login />} />

              {/* Signup Access Gate */}
              <Route path="/signup-access" element={<PasswordGate />} />

              {/* Signup Route */}
              <Route path="/signup" element={<Signup />} />

              {/* Signup finalization remediation */}
              <Route path="/signup-recovery" element={<SignupFinalizationRecovery />} />

              {/* Email action handler (verification + password reset) */}
              <Route path="/auth/action" element={<AuthActionHandler />} />
              <Route path="/verify-email" element={<VerifyEmailRedirect />} />

              {/* Profile Completion for OAuth Users */}
              <Route
                path="/complete-profile"
                element={
                  <ProtectedRoute>
                    <ProfileCompletion />
                  </ProtectedRoute>
                }
              />

              {/* Public Homepage */}
              <Route path="/" element={<HomePage />} />

              {/* Legal Pages */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />

              {/* Catch all - redirect to homepage */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </Router>
    </AuthProvider>
  );
}

export default App;
